import {
  UseMutateAsyncFunction,
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  DeleteDraftTableRowDto,
  DraftDataDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_DATA } from "../GetFormDraftData/useGetFormDraftData";

import { submissionsApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_DELETE_TABLE_ROW = "DELETE_TABLE_ROW";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

const deleteDraftTableRow = async (
  req: DeleteDraftTableRowDto,
): Promise<string[]> => {
  const response =
    await submissionsApi.submissionsControllerDeleteDraftTableRow(req);

  return response.data;
};

export const useDeleteDraftTableRow = (
  user: UserProfileDto | null,
  params: DeleteDraftTableRowDto,
): {
  deleteDraftTableRow: UseMutateFunction<string[], Error>;
  deleteDraftTableRowAsync: UseMutateAsyncFunction<string[], Error>;
  deleteDraftTableRowEnabled: boolean;
  deleteDraftTableRowError: Error | null;
  isSuccessDeleteDraftTableRow: boolean;
  isPendingDeleteDraftTableRow: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending, error } = useMutation({
    mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_DELETE_TABLE_ROW, params],
    mutationFn: enabled
      ? (): Promise<string[]> => deleteDraftTableRow(params)
      : undefined,
    onSuccess(data) {
      const queryKey = [
        DOMAIN_FORM_SUBMISSIONS,
        ACTION_GET_FORM_DRAFT_DATA,
        params.formId,
      ];
      const currentData = queryClient.getQueryData<DraftDataDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData ? currentData.filter((d) => !data.includes(d.id)) : [];
      });
    },
  });

  return {
    deleteDraftTableRow: mutate,
    deleteDraftTableRowAsync: mutateAsync,
    deleteDraftTableRowEnabled: enabled,
    deleteDraftTableRowError: error,
    isSuccessDeleteDraftTableRow: isSuccess,
    isPendingDeleteDraftTableRow: isPending,
  };
};
